// ===========================================================================
// SFU client (mediasoup-client).
//
// Sends the browser's audio/video to the server's SFU and receives every other
// peer's tracks back, grouped into one MediaStream PER peer (so a 1:1 call shows
// one remote video and a group room shows a grid). File transfers ride an SCTP
// data channel through the SFU (one DataProducer per peer, broadcast to the rest).
// Neither peer ever learns another's IP, and the server is in the media path
// (the seam for moderating minors' live video). ASCII only.
// ===========================================================================

import { Device } from 'mediasoup-client';


export class SfuPeer {
  /**
   * @param {(op: string, args?: object) => Promise<any>} rpc  one SFU request -> its reply
   * @param {{ localStream?: MediaStream }} opts
   * @param {{ onPeerStream?: (peerId: string, s: MediaStream) => void,
   *           onPeerGone?: (peerId: string) => void,
   *           onFile?: (peerId: string, file: { name: string, mime: string, blob: Blob }) => void,
   *           onProduceRefused?: (kind: string, err: Error) => void,
   *           onState?: (s: string) => void }} cb
   */
  constructor(rpc, opts, cb) {
    this.rpc = rpc;
    this.localStream = opts.localStream || null;
    this.maxBitrate = opts.maxBitrate || 0; // personal video bitrate ceiling (Plus perk)
    this.cb = cb || {};
    this.device = null;
    this.sendTransport = null;
    this.recvTransport = null;
    this.producers = [];
    this.consumers = new Map(); // consumerId -> consumer
    this.consumerPeer = new Map(); // consumerId -> peerId
    this.peerStreams = new Map(); // peerId -> MediaStream
    this.dataConsumers = new Map(); // dataConsumerId -> dataConsumer
    this.consumedProducers = new Set(); // producerIds already consumed (idempotency)
    this.pendingProducers = []; // newProducer events that arrived before we were ready
    this.ready = false; // true once the recv transport exists
    this.closed = false;
  }

  async start() {
    const { rtpCapabilities, producers, dataProducers } = await this.rpc('join');
    this.device = new Device();
    await this.device.load({ routerRtpCapabilities: rtpCapabilities });
    await this._createSendTransport();
    await this._createRecvTransport();
    this.ready = true; // recv transport exists -> newProducer events can be consumed
    await this._produceLocal();
    // NO file data channel. Files go through POST /api/share so the server can
    // classify them before delivery, and the SFU refuses produceData outright --
    // asking for one here would reject and take the whole join down with it.
    for (const p of producers || []) {
       
      await this._consume(p.producerId, p.peerId);
    }
    // Drain newProducer events that arrived while we were still setting up (the other
    // peer produced before our recv transport existed). Without this, that track is
    // lost and one side's video never appears -- the intermittent black-tile bug.
    const queued = this.pendingProducers.splice(0);
    for (const p of queued) {
       
      await this._consume(p.producerId, p.peerId);
    }
    for (const dp of dataProducers || []) {
       
      await this._consumeData(dp.dataProducerId, dp.peerId);
    }
  }

  async _createSendTransport() {
    const params = await this.rpc('createTransport', { direction: 'send' });
    const t = this.device.createSendTransport(params);
    t.on('connect', ({ dtlsParameters }, ok, fail) => {
      this.rpc('connectTransport', { transportId: t.id, dtlsParameters }).then(() => ok(), fail);
    });
    t.on('produce', ({ kind, rtpParameters }, ok, fail) => {
      this.rpc('produce', { transportId: t.id, kind, rtpParameters }).then(({ id }) => ok({ id }), fail);
    });
    t.on('producedata', ({ sctpStreamParameters, label, protocol }, ok, fail) => {
      this.rpc('produceData', { transportId: t.id, sctpStreamParameters, label, protocol }).then(({ id }) => ok({ id }), fail);
    });
    t.on('connectionstatechange', (s) => this.cb.onState?.(s));
    this.sendTransport = t;
  }

  async _createRecvTransport() {
    const params = await this.rpc('createTransport', { direction: 'recv' });
    const t = this.device.createRecvTransport(params);
    t.on('connect', ({ dtlsParameters }, ok, fail) => {
      this.rpc('connectTransport', { transportId: t.id, dtlsParameters }).then(() => ok(), fail);
    });
    t.on('connectionstatechange', (s) => this.cb.onState?.(s));
    this.recvTransport = t;
  }

  async _produceLocal() {
    if (!this.localStream || !this.sendTransport) return;
    for (const track of this.localStream.getTracks()) {
      // stopTracks: false -- mediasoup-client stops the track when the producer
      // closes by DEFAULT. The same camera/mic stream is reused across matches (and
      // is the self-view), so stopping it on teardown freezes our self-view and makes
      // the next partner see black. We own the track; cleanup() stops it on leave.
      const produceOpts = { track, stopTracks: false };
      // Lumi Plus raises the personal video bitrate ceiling.
      if (track.kind === 'video' && this.maxBitrate) {
        produceOpts.encodings = [{ maxBitrate: this.maxBitrate }];
      }
      try {
         
        const producer = await this.sendTransport.produce(produceOpts);
        this.producers.push(producer);
      } catch (err) {
        // One refused track must not take the others down: a room with every
        // camera slot taken still wants your voice. The app hears about it.
        this.cb.onProduceRefused?.(track.kind, err);
      }
    }
  }

  /** Whether a track of this kind is being sent right now. */
  hasProducer(kind) {
    return this.producers.some((p) => p.kind === kind && !p.closed);
  }

  /**
   * Stop sending a kind: the camera going off in a group room. Closing the
   * producer on the CLIENT alone tells the server nothing -- mediasoup-client
   * does not signal it -- so the server is asked to close it too, which is
   * what frees the video slot and drops the track from everyone else's tile.
   */
  async closeKind(kind) {
    const mine = this.producers.filter((p) => p.kind === kind && !p.closed);
    this.producers = this.producers.filter((p) => !mine.includes(p));
    for (const p of mine) {
      try {
        p.close();
      } catch {
        /* ignore */
      }
       
      await this.rpc('closeProducer', { producerId: p.id }).catch(() => {});
    }
    return mine.length;
  }

  /**
   * Produce a single new track mid-call (e.g. a text/audio user turning on their
   * camera). The partner gets a newProducer event and consumes it -- no rematch.
   */
  async addTrack(track) {
    if (this.closed || !this.sendTransport) return null;
    const opts = { track, stopTracks: false };
    if (track.kind === 'video' && this.maxBitrate) opts.encodings = [{ maxBitrate: this.maxBitrate }];
    const producer = await this.sendTransport.produce(opts);
    this.producers.push(producer);
    return producer;
  }

  /** Swap the live track of an already-produced kind (e.g. raw camera <-> effects canvas). */
  async replaceTrack(kind, track) {
    const producer = this.producers.find((p) => p.kind === kind && !p.closed);
    if (!producer) return false;
    try {
      await producer.replaceTrack({ track });
      return true;
    } catch {
      return false;
    }
  }

  _streamFor(peerId) {
    let s = this.peerStreams.get(peerId);
    if (!s) {
      s = new MediaStream();
      this.peerStreams.set(peerId, s);
    }
    return s;
  }

  async _consume(producerId, peerId) {
    if (this.closed || !this.recvTransport) return;
    if (this.consumedProducers.has(producerId)) return; // idempotent: never double-consume
    this.consumedProducers.add(producerId);
    const id = peerId || 'peer';
    let consumer;
    try {
      const data = await this.rpc('consume', { producerId, rtpCapabilities: this.device.rtpCapabilities });
      consumer = await this.recvTransport.consume({
        id: data.id,
        producerId: data.producerId,
        kind: data.kind,
        rtpParameters: data.rtpParameters,
      });
    } catch (err) {
      this.consumedProducers.delete(producerId); // failed -> allow a later retry
      throw err;
    }
    this.consumers.set(consumer.id, consumer);
    this.consumerPeer.set(consumer.id, id);
    const stream = this._streamFor(id);
    stream.addTrack(consumer.track);
    this.cb.onPeerStream?.(id, stream);
    // The consumer starts paused; resume it (retry once) so a live producer is never
    // left paused -> a black/frozen tile despite media flowing.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
         
        await this.rpc('resumeConsumer', { consumerId: consumer.id });
        return;
      } catch (err) {
        if (attempt === 1) throw err;
      }
    }
  }

  async onNewProducer(producerId, peerId) {
    // If the recv transport is not ready yet, queue it -- start() will consume it.
    // Dropping it here is exactly what made one side's video intermittently vanish.
    if (!this.ready || !this.recvTransport) {
      this.pendingProducers.push({ producerId, peerId });
      return;
    }
    try {
      await this._consume(producerId, peerId);
    } catch {
      /* ignore -- rare; the consumed set was already rolled back for a retry */
    }
  }

  onProducerClosed(producerId) {
    this.consumedProducers.delete(producerId);
    this.pendingProducers = this.pendingProducers.filter((p) => p.producerId !== producerId);
    for (const [cid, c] of this.consumers) {
      if (c.producerId !== producerId) continue;
      const peerId = this.consumerPeer.get(cid) || 'peer';
      const stream = this.peerStreams.get(peerId);
      try {
        if (stream) stream.removeTrack(c.track);
      } catch {
        /* ignore */
      }
      try {
        c.close();
      } catch {
        /* ignore */
      }
      this.consumers.delete(cid);
      this.consumerPeer.delete(cid);
      if (stream && stream.getTracks().length === 0) {
        this.peerStreams.delete(peerId);
        this.cb.onPeerGone?.(peerId);
      } else if (stream) {
        this.cb.onPeerStream?.(peerId, stream);
      }
    }
  }

  removePeer(peerId) {
    for (const [cid, c] of this.consumers) {
      if (this.consumerPeer.get(cid) === peerId) {
        this.consumedProducers.delete(c.producerId); // allow re-consume if they return
        try {
          c.close();
        } catch {
          /* ignore */
        }
        this.consumers.delete(cid);
        this.consumerPeer.delete(cid);
      }
    }
    if (this.peerStreams.delete(peerId)) this.cb.onPeerGone?.(peerId);
  }

  // Files are NOT sent over the data channel any more: they go to
  // POST /api/share, where the server classifies them before the other side can
  // fetch them. _consumeData stays only to drain a channel opened by an older
  // peer; the server refuses new ones.

  async _consumeData(dataProducerId, peerId) {
    if (this.closed || !this.recvTransport) return;
    const id = peerId || 'peer';
    const data = await this.rpc('consumeData', { dataProducerId });
    const dc = await this.recvTransport.consumeData({
      id: data.id,
      dataProducerId: data.dataProducerId,
      sctpStreamParameters: data.sctpStreamParameters,
      label: data.label,
      protocol: data.protocol,
    });
    this.dataConsumers.set(dc.id, dc);
    let incoming = null; // { name, mime, size, parts, received }
    let chain = Promise.resolve(); // serialize so async (Blob) conversion keeps order
    const handle = async (msg) => {
      if (typeof msg === 'string') {
        try {
          const m = JSON.parse(msg);
          if (m.kind === 'meta') incoming = { name: m.name, mime: m.mime, size: m.size, parts: [], received: 0 };
        } catch {
          /* ignore */
        }
        return;
      }
      if (!incoming) return;
      let u8;
      if (msg instanceof ArrayBuffer) u8 = new Uint8Array(msg);
      else if (typeof Blob !== 'undefined' && msg instanceof Blob) u8 = new Uint8Array(await msg.arrayBuffer());
      else if (ArrayBuffer.isView(msg)) u8 = new Uint8Array(msg.buffer, msg.byteOffset, msg.byteLength);
      else return;
      incoming.parts.push(u8);
      incoming.received += u8.byteLength;
      if (incoming.received >= incoming.size) {
        const blob = new Blob(incoming.parts, { type: incoming.mime || 'application/octet-stream' });
        this.cb.onFile?.(id, { name: incoming.name, mime: incoming.mime, blob });
        incoming = null;
      }
    };
    dc.on('message', (msg) => {
      chain = chain.then(() => handle(msg));
    });
  }

  async onNewDataProducer(dataProducerId, peerId) {
    try {
      await this._consumeData(dataProducerId, peerId);
    } catch {
      /* ignore */
    }
  }

  close() {
    this.closed = true;
    for (const p of this.producers) {
      try {
        p.close();
      } catch {
        /* ignore */
      }
    }
    for (const c of this.consumers.values()) {
      try {
        c.close();
      } catch {
        /* ignore */
      }
    }
    for (const dc of this.dataConsumers.values()) {
      try {
        dc.close();
      } catch {
        /* ignore */
      }
    }
    try {
      this.dataProducer?.close();
    } catch {
      /* ignore */
    }
    try {
      this.sendTransport?.close();
    } catch {
      /* ignore */
    }
    try {
      this.recvTransport?.close();
    } catch {
      /* ignore */
    }
    this.producers = [];
    this.consumers.clear();
    this.consumerPeer.clear();
    this.peerStreams.clear();
    this.dataConsumers.clear();
    this.consumedProducers.clear();
    this.pendingProducers = [];
    this.ready = false;
    this.dataProducer = null;
  }
}

export default SfuPeer;
